<template>
  <v-dialog v-model="dialog" persistent max-width="500px">
    <v-form ref="form">
      <v-card>
        <v-toolbar dense dark color="primary">
          <v-toolbar-title>Edit Location Name</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="close()">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <p>
            Enter a new display name, or select an existing alias to become the primary name.
          </p>
          <v-combobox ref="displayNameInput" v-model="formObject.DisplayName" solo label="Location Name" :items="aliasItems" item-text="name" item-value="name" :rules="[rules.required]" persistent-hint hint="Locations with identical names are treated as separate locations." :return-object="false" />

          <template v-if="isSystemAdmin">
            <v-subheader class="pl-0">System Administrator Settings</v-subheader>
            <v-checkbox color="info" class="mt-0" v-model="formObject.data.makePrimary" label="Make primary name" persistent-hint hint="New synonym will become the primary name of all data sources" />
          </template>
        </v-card-text>
        <v-alert key="alert" outline class="my-0" type="warning" :value="!formObject.data.makePrimary">
          <div class="primary--text">
            The data source alias shows up in reports, which may cause confusion when the primary alias does not match
            the
            location name.
          </div>
        </v-alert>
        <v-card-actions>
          <v-spacer />
          <v-btn color="success" @click="submit()" :loading="loading.submit">
            <v-icon class="mr-1">done</v-icon>{{ $t('Button.Save') }}
          </v-btn>
          <v-btn depressed @click="close()">{{ $t('Button.Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'

  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')
  const app = createNamespacedHelpers('app')
  const authenticate = createNamespacedHelpers('authenticate')

  import {
    SOURCE_TYPES_WITH_NAME,
  } from '@/store/MonitoringFeature/monitoringfeature'

  export default {
    data: () => ({
      dialog: false,
      nameFromSource: null,
      formObject: {
        data: {},
      },
      initialObject: {
        DisplayName: null,
        data: {
          makePrimary: true,
        },
      },

      loading: {
        submit: false,
      },
      rules: {
        required: (v) => !!v || 'Required',
      },
    }),
    computed: {
      ...monitoringFeatures.mapGetters([
        'monitoringFeatures',
        'currentMonitoringFeature',
        'featureDataSourcesIndexByType',
        'featureDataSourceIndexByTypeAndId',
        'getSourceName',
      ]),
      ...dataSources.mapGetters([
        'dataSourceIndexByMF',
      ]),
      ...authenticate.mapGetters(['isSystemAdmin']),
      sourceTypesWithNameIndex() {
        let index = {}
        SOURCE_TYPES_WITH_NAME.forEach(sourceType => index[sourceType.key] = sourceType)
        return index
      },
      aliasesAvailable() {
        let result = []
        if (!this.currentMonitoringFeature) return result

        let mfId = this.currentMonitoringFeature.Id
        let dataSources = this.dataSourceIndexByMF[mfId]
        if (dataSources == null) {
          return result
        }
        let synonyms = dataSources.filter(ds => !!ds.Synonyms).map(ds => ds.Synonyms)
        synonyms = synonyms.flatten()
        return synonyms
      },
      aliasItems() {
        let index = new Set()
        this.aliasesAvailable.forEach(alias => index.add(alias.Name))

        let result = []

        result.push({
          header: 'Aliases available',
        })

        let items = [...index].map(name => ({
          name,
        }))

        result.push(...items)
        return result
      },
    },
    methods: {
      ...monitoringFeatures.mapActions(['updateMonitoringFeature']),
      ...dataSources.mapActions(['fetchDataSources']),
      ...app.mapActions(['setSnackbar']),
      show(monitoringFeature) {
        this.dialog = true
        this.formObject = {
          ...JSON.parse(JSON.stringify(this.initialObject)),
          ...JSON.parse(JSON.stringify(monitoringFeature)),
        }
      },
      close() {
        this.dialog = false
      },
      async submit() {
        this.$refs.displayNameInput.blur()
        await this.$nextTick()

        if (!this.$refs.form.validate()) return

        this.loading.submit = true

        try {
          await this.updateMonitoringFeature({
            monitoringFeature: this.formObject,
            data: this.formObject.data,
          })

          await this.fetchDataSources()

          this.setSnackbar({
            text: 'Location name successfully updated.',
            color: 'success',
            icon: 'done',
          })

          this.close()
        } catch (error) {
          console.error(error)
          this.setSnackbar({
            text: error,
            color: 'error',
            icon: 'error',
          })
        }

        this.loading.submit = false
      },
    },
  }
</script>